import { ATTACHMENT_IDS, INLINE_IDS } from './fixtures.ts';
import { cloneSnapshot, type OperationResult, type Snapshot } from './model.ts';
import type { QuizAnswers } from './quiz-model.ts';
import {
  DEMO_RECOMMENDATION_CONFIG, type DemoRecommendationConfig, type DemoTemplate,
} from './recommendation-config.ts';

export type ResolvedDemoTemplate = DemoTemplate & {
  attachment: { componentId: string; anchorIndex: number } | null;
};
export type DemoRecommendationResult =
  | {
    status: 'ready'; quizVersion: string; mappingVersion: string; catalogVersion: string;
    primary: ResolvedDemoTemplate; alternative: ResolvedDemoTemplate; explanations: string[];
  }
  | {
    status: 'unavailable'; quizVersion: string; mappingVersion: string; catalogVersion: string;
    unavailableReasons: string[];
  };

function unavailable(config: DemoRecommendationConfig, reason: string): DemoRecommendationResult {
  return {
    status: 'unavailable', quizVersion: config.quizVersion, mappingVersion: config.mappingVersion,
    catalogVersion: config.catalogVersion, unavailableReasons: [reason],
  };
}

function validTemplate(template: DemoTemplate | undefined): template is DemoTemplate {
  return Boolean(template && template.inlineComponentIds.length > 0
    && template.inlineComponentIds.every((id) => INLINE_IDS.has(id))
    && Number.isInteger(template.charmAnchorIndex) && template.charmAnchorIndex >= 0
    && template.charmAnchorIndex < template.inlineComponentIds.length);
}

export function buildDemoRecommendation(
  answers: QuizAnswers,
  config: DemoRecommendationConfig = DEMO_RECOMMENDATION_CONFIG,
): DemoRecommendationResult {
  if (config.reviewStatus !== 'demo' || config.quizVersion !== DEMO_RECOMMENDATION_CONFIG.quizVersion
    || config.mappingVersion !== DEMO_RECOMMENDATION_CONFIG.mappingVersion
    || config.catalogVersion !== DEMO_RECOMMENDATION_CONFIG.catalogVersion) {
    return unavailable(config, 'The demo configuration version is unknown. Continue directly to DIY.');
  }
  const visual = answers.q_visual_balance?.[0];
  if (visual !== 'soft' && visual !== 'earth' && visual !== 'contrast') {
    return unavailable(config, 'No supported demo visual direction was selected. Continue directly to DIY.');
  }
  const detailCodes = answers.q_detail_level ?? [];
  if (detailCodes.some((code) => !['repetition', 'center', 'small_charm'].includes(code))) {
    return unavailable(config, 'A demo detail option is not recognized. Nothing was substituted.');
  }
  const mapping = config.visualMappings[visual];
  if (!mapping) return unavailable(config, 'No demo mapping exists for that visual direction. Nothing was substituted.');
  const preferFocus = detailCodes.includes('center');
  const primaryId = preferFocus ? mapping.focus : mapping.rhythm;
  const alternativeId = preferFocus ? mapping.rhythm : mapping.focus;
  const primaryBase = config.templates.find((template) => template.id === primaryId);
  const alternativeBase = config.templates.find((template) => template.id === alternativeId);
  if (!validTemplate(primaryBase) || !validTemplate(alternativeBase) || primaryBase.id === alternativeBase.id) {
    return unavailable(config, 'No complete synthetic template is available. Nothing was substituted.');
  }
  const includeCharm = detailCodes.includes('small_charm');
  if (includeCharm && !ATTACHMENT_IDS.has('demo_star_charm')) {
    return unavailable(config, 'The selected demo detail is unavailable. Nothing was substituted.');
  }
  const resolve = (template: DemoTemplate): ResolvedDemoTemplate => ({
    ...template,
    inlineComponentIds: [...template.inlineComponentIds],
    attachment: includeCharm ? { componentId: 'demo_star_charm', anchorIndex: template.charmAnchorIndex } : null,
  });
  const explanations = [
    visual === 'soft' ? 'You chose the Soft tonal color study.' : visual === 'earth' ? 'You chose the Warm earth color study.' : 'You chose the Gentle contrast color study.',
  ];
  if (detailCodes.includes('repetition')) explanations.push('You selected a calm repeating rhythm.');
  if (detailCodes.includes('center')) explanations.push('You selected one defined focal point.');
  if (includeCharm) explanations.push('You selected a small hanging detail.');
  return {
    status: 'ready', quizVersion: config.quizVersion, mappingVersion: config.mappingVersion,
    catalogVersion: config.catalogVersion, primary: resolve(primaryBase), alternative: resolve(alternativeBase), explanations,
  };
}

export function applyDemoTemplate(snapshot: Snapshot, template: ResolvedDemoTemplate): OperationResult {
  if (!validTemplate(template) || (template.attachment && (
    !ATTACHMENT_IDS.has(template.attachment.componentId)
    || template.attachment.anchorIndex < 0 || template.attachment.anchorIndex >= template.inlineComponentIds.length
  ))) return { snapshot, error: 'This synthetic template is incomplete and was not applied.' };
  const requiredIds = template.inlineComponentIds.length + (template.attachment ? 1 : 0);
  if (!Number.isSafeInteger(snapshot.nextUid) || snapshot.nextUid < 1 || snapshot.nextUid > Number.MAX_SAFE_INTEGER - requiredIds) {
    return { snapshot, error: 'New design instance IDs could not be allocated safely.' };
  }
  const next = cloneSnapshot(snapshot);
  let sequence = snapshot.nextUid;
  next.design.inline_items = template.inlineComponentIds.map((componentId) => ({
    uid: `bead-${(sequence++).toString(36)}`, component_id: componentId, orientation_deg: 0,
  }));
  next.design.attachments = template.attachment ? [{
    uid: `charm-${(sequence++).toString(36)}`,
    component_id: template.attachment.componentId,
    anchor_uid: next.design.inline_items[template.attachment.anchorIndex].uid,
    orientation_deg: 0,
  }] : [];
  next.selectedInlineUid = next.design.inline_items[0]?.uid ?? null;
  next.nextUid = sequence;
  return { snapshot: next };
}
